
import React, { useRef, useState } from 'react';
import { AppConfig, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface SecurityTabProps {
  config: AppConfig;
  language: Language;
  onUpdate: (config: Partial<AppConfig>) => void;
  onDeleteAccount?: () => void;
  onStartScanning: () => void;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ config, language, onUpdate, onDeleteAccount, onStartScanning }) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';
  const [apiKeyInput, setApiKeyInput] = useState(config.apiKey || '');
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const apiKeyInputRef = useRef<HTMLInputElement>(null);

  const handleSaveApiKey = () => {
    onUpdate({ apiKey: apiKeyInput });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  // Sync internal input state when prop changes, if needed
  React.useEffect(() => {
    if (config.apiKey !== apiKeyInput && config.apiKey) {
        setApiKeyInput(config.apiKey);
    }
  }, [config.apiKey]);

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-6">
       <div className="bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-[11px] font-black text-slate-800 dark:text-white mb-1">{t.apiStatus}</h4>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${config.apiKey ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                {config.apiKey ? t.apiKeyActive : t.apiKeyMissing}
              </span>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-white/10 flex items-center justify-center text-slate-500">
               <i className="fas fa-key"></i>
            </div>
          </div>

          <div className="space-y-3">
             <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.manageKeys}</label>
             <div className="relative">
                <input 
                   ref={apiKeyInputRef}
                   type={isKeyVisible ? "text" : "password"} 
                   value={apiKeyInput}
                   onChange={(e) => setApiKeyInput(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && handleSaveApiKey()}
                   placeholder={t.enterKeyPlaceholder}
                   className={`w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl py-3 ${isRtl ? 'pr-10 pl-10' : 'pl-10 pr-10'} text-xs font-mono dark:text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all`}
                />
                {/* Barcode/Scan Button */}
                <button 
                   onClick={onStartScanning}
                   title={t.scanBarcode}
                   className={`absolute ${isRtl ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-500 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg transition-all`}
                >
                   <i className="fas fa-barcode text-sm"></i>
                </button>
                
                {/* Toggle Visibility */}
                <button 
                   onClick={() => setIsKeyVisible(!isKeyVisible)}
                   className={`absolute ${isRtl ? 'right-2' : 'left-2'} top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all`}
                >
                   <i className={`fas ${isKeyVisible ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                </button>
             </div>
             <button 
                onClick={handleSaveApiKey} 
                className={`w-full py-3 rounded-xl text-[10px] font-black transition-all flex items-center justify-center gap-2 ${saveSuccess ? 'bg-emerald-500 text-white' : 'bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90'}`}
             >
                {saveSuccess ? <><i className="fas fa-check"></i> {t.keySaved}</> : <><i className="fas fa-save"></i> {t.saveKey}</>}
             </button>
          </div>
       </div>

       <div className="pt-4 border-t border-slate-100 dark:border-white/5">
         <button onClick={onDeleteAccount} className="w-full py-3 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-xl text-[10px] font-black border border-rose-500/20 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-all flex items-center justify-center gap-2">
           <i className="fas fa-trash-alt"></i>
           {t.deleteAccount}
         </button>
       </div>
    </div>
  );
};
export default SecurityTab;
